import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { invalidateEntity } from '../cache-invalidation';
import { orgMemberKeys } from '../query-keys';
import {
  addOrgMemberContract,
  updateOrgMemberContract,
  deleteOrgMemberContract,
  sendOrgMemberInvitationContract,
  bulkSendOrgMemberInvitationsContract,
  suspendOrgMemberContract,
  unsuspendOrgMemberContract,
  resetOrgMemberSignInContract,
  type AddOrgMemberRequest,
  type UpdateOrgMemberRequest,
  type AddOrgMemberResponse,
  type UpdateOrgMemberResponse,
  type DeleteOrgMemberResponse,
  type SendOrgMemberInvitationResponse,
  type BulkSendOrgMemberInvitationsResponse,
  type SuspendOrgMemberResponse,
  type UnsuspendOrgMemberResponse,
  type ResetIdentitySignInResponse
} from '@bliks-io/contracts';

/**
 * Create organization member mutation
 *
 * Creates a new organization member.
 * - Invalidates every query family that may hold a populated copy — OrgFunction
 *   membership is derived from user docs, so a new member with mainRole or
 *   secondaryRoles changes member lists and counts
 * - Returns the newly created member
 *
 * @example
 * ```tsx
 * function CreateMemberForm() {
 *   const createMember = useCreateOrgMember();
 *
 *   const handleSubmit = async (data: CreateOrgMemberRequest) => {
 *     try {
 *       const result = await createMember.mutateAsync(data);
 *       toast.success('Member created successfully');
 *     } catch (error) {
 *       toast.error('Failed to create member');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={createMember.isPending}>Create Member</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useCreateOrgMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddOrgMemberRequest): Promise<AddOrgMemberResponse> => {
      const { data: response } = await apiClient.post<AddOrgMemberResponse>(
        addOrgMemberContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'orgMember');
    }
  });
}

/**
 * Update organization member mutation
 *
 * Updates an existing organization member by ID.
 * - Invalidates both list and detail caches
 * - Returns the updated member
 *
 * @example
 * ```tsx
 * function EditMemberForm({ memberId }: { memberId: string }) {
 *   const updateMember = useUpdateOrgMember();
 *
 *   const handleSubmit = async (data: UpdateOrgMemberRequest) => {
 *     try {
 *       await updateMember.mutateAsync({ id: memberId, data });
 *       toast.success('Member updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update member');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={updateMember.isPending}>Update Member</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useUpdateOrgMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateOrgMemberRequest;
    }): Promise<UpdateOrgMemberResponse> => {
      const path = updateOrgMemberContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<UpdateOrgMemberResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'orgMember');
    }
  });
}

/**
 * Delete organization member mutation
 *
 * Deletes an organization member by ID.
 * - Invalidates org members list cache
 * - Removes member detail from cache
 *
 * @example
 * ```tsx
 * function DeleteMemberButton({ memberId }: { memberId: string }) {
 *   const deleteMember = useDeleteOrgMember();
 *
 *   const handleDelete = async () => {
 *     if (!confirm('Are you sure?')) return;
 *
 *     try {
 *       await deleteMember.mutateAsync(memberId);
 *       toast.success('Member deleted successfully');
 *     } catch (error) {
 *       toast.error('Failed to delete member');
 *     }
 *   };
 *
 *   return (
 *     <Button
 *       variant="destructive"
 *       loading={deleteMember.isPending}
 *       onClick={handleDelete}
 *     >
 *       Delete
 *     </Button>
 *   );
 * }
 * ```
 */
export function useDeleteOrgMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteOrgMemberResponse> => {
      const path = deleteOrgMemberContract.path.replace(':id', id);
      const { data } = await apiClient.delete<DeleteOrgMemberResponse>(path);
      return data;
    },
    onSuccess: (_, id) => {
      invalidateEntity(queryClient, 'orgMember');
      queryClient.removeQueries({ queryKey: orgMemberKeys.detail(id) });
    }
  });
}

/**
 * Send organization member invitation mutation
 *
 * Sends an invitation email to an inactive organization member.
 * - Changes status from 'inactive' to 'pending'
 * - Sends invitation email with activation link
 * - Invalidates both list and detail caches
 *
 * @example
 * ```tsx
 * function SendInvitationButton({ memberId }: { memberId: string }) {
 *   const sendInvitation = useSendOrgMemberInvitation();
 *
 *   const handleSend = async () => {
 *     try {
 *       await sendInvitation.mutateAsync(memberId);
 *       toast.success('Invitation sent successfully');
 *     } catch (error) {
 *       toast.error('Failed to send invitation');
 *     }
 *   };
 *
 *   return (
 *     <Button
 *       loading={sendInvitation.isPending}
 *       onClick={handleSend}
 *     >
 *       Send Invitation
 *     </Button>
 *   );
 * }
 * ```
 */
export function useSendOrgMemberInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: orgMemberKeys.sendInvitationMutation(),
    mutationFn: async (id: string): Promise<SendOrgMemberInvitationResponse> => {
      const path = sendOrgMemberInvitationContract.path.replace(':id', id);
      const { data } = await apiClient.post<SendOrgMemberInvitationResponse>(path);
      return data;
    },
    // onSettled, not onSuccess: the status flips server-side before the mail
    // dispatch, so a failed dispatch must still refresh member + seat caches.
    // Returned, so the row stays busy until the refetched list shows the new status.
    onSettled: () => invalidateEntity(queryClient, 'orgMember', { awaitKey: orgMemberKeys.all })
  });
}

/**
 * Bulk send organization member invitations mutation
 *
 * Sends invitation emails to a batch of inactive organization members.
 * - Each invited member's status flips from 'inactive' to 'pending'
 * - Returns the invited ids and any skipped (with reason)
 * - Invalidates the org members cache so badges refetch
 *
 * @example
 * ```tsx
 * const bulkInvite = useBulkSendOrgMemberInvitations();
 * const res = await bulkInvite.mutateAsync(inactiveIds);
 * toast.success(`Sent ${res.data.invitedUserIds.length} invitation(s)`);
 * ```
 */
export function useBulkSendOrgMemberInvitations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userIds: string[]): Promise<BulkSendOrgMemberInvitationsResponse> => {
      const { data } = await apiClient.post<BulkSendOrgMemberInvitationsResponse>(
        bulkSendOrgMemberInvitationsContract.path,
        { userIds }
      );
      return data;
    },
    // onSettled, not onSuccess: statuses flip server-side before the mail
    // dispatches, so a failed batch must still refresh member + seat caches.
    onSettled: () => {
      invalidateEntity(queryClient, 'orgMember');
    }
  });
}

/**
 * Suspend organization member mutation
 *
 * Suspends an active or pending organization member.
 * - Changes status to 'suspended'
 * - User loses access to the system
 * - Invalidates both list and detail caches
 *
 * @example
 * ```tsx
 * function SuspendMemberButton({ memberId }: { memberId: string }) {
 *   const suspendMember = useSuspendOrgMember();
 *
 *   const handleSuspend = async () => {
 *     if (!confirm('Suspend this member?')) return;
 *
 *     try {
 *       await suspendMember.mutateAsync(memberId);
 *       toast.success('Member suspended successfully');
 *     } catch (error) {
 *       toast.error('Failed to suspend member');
 *     }
 *   };
 *
 *   return (
 *     <Button
 *       variant="destructive"
 *       loading={suspendMember.isPending}
 *       onClick={handleSuspend}
 *     >
 *       Suspend
 *     </Button>
 *   );
 * }
 * ```
 */
export function useSuspendOrgMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: orgMemberKeys.suspendMutation(),
    mutationFn: async (id: string): Promise<SuspendOrgMemberResponse> => {
      const path = suspendOrgMemberContract.path.replace(':id', id);
      const { data } = await apiClient.post<SuspendOrgMemberResponse>(path);
      return data;
    },
    // Returned, so the row stays busy until the refetched list shows the new status.
    onSuccess: () => invalidateEntity(queryClient, 'orgMember', { awaitKey: orgMemberKeys.all })
  });
}

/**
 * Unsuspend organization member mutation
 *
 * Unsuspends a suspended organization member.
 * - Restores status based on timestamps (active/pending/inactive)
 * - User regains access to the system
 * - Invalidates both list and detail caches
 *
 * @example
 * ```tsx
 * function UnsuspendMemberButton({ memberId }: { memberId: string }) {
 *   const unsuspendMember = useUnsuspendOrgMember();
 *
 *   const handleUnsuspend = async () => {
 *     try {
 *       await unsuspendMember.mutateAsync(memberId);
 *       toast.success('Member unsuspended successfully');
 *     } catch (error) {
 *       toast.error('Failed to unsuspend member');
 *     }
 *   };
 *
 *   return (
 *     <Button
 *       loading={unsuspendMember.isPending}
 *       onClick={handleUnsuspend}
 *     >
 *       Unsuspend
 *     </Button>
 *   );
 * }
 * ```
 */
export function useUnsuspendOrgMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: orgMemberKeys.unsuspendMutation(),
    mutationFn: async (id: string): Promise<UnsuspendOrgMemberResponse> => {
      const path = unsuspendOrgMemberContract.path.replace(':id', id);
      const { data } = await apiClient.post<UnsuspendOrgMemberResponse>(path);
      return data;
    },
    // Returned, so the row stays busy until the refetched list shows the new status.
    onSuccess: () => invalidateEntity(queryClient, 'orgMember', { awaitKey: orgMemberKeys.all })
  });
}

/**
 * Reset organization member sign-in mutation
 *
 * Replaces the member's sign-in account and emails them a setup link. Every
 * session they hold ends.
 * - Invalidates both list and detail caches
 */
export function useResetOrgMemberSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<ResetIdentitySignInResponse> => {
      const path = resetOrgMemberSignInContract.path.replace(':id', id);
      const { data } = await apiClient.post<ResetIdentitySignInResponse>(path);
      return data;
    },
    // onSettled, not onSuccess: the identity is reset server-side before
    // fallible late steps, so a failed request must still refresh the list.
    onSettled: () => {
      invalidateEntity(queryClient, 'orgMember');
    }
  });
}
